import { useState } from 'react';
import { Checkbox, Container, Header, Segment } from 'semantic-ui-react';

import { ResponsiveContainer } from '../../ResponsiveContainer';

import { ClassicPersonCreator } from './ClassicPersonCreator';
import { NewPersonCreator } from './NewPersonCreator';

import './PersonCreator.css';

/**
 * "Add person", in two forms.
 *
 * The classic form is the one Centre Managers use every day; the new registration form captures
 * consent, guardian details for minors and supporting documents. **Both are live at once**,
 * chosen by the toggle in the corner of the card, because changing a daily data-entry workflow
 * mid-season is how you lose a season's data quality.
 *
 * They share one page shell and one `adminCreateUser` mutation — the registration arguments are
 * all optional server-side and the classic form simply sends none of them. That is what stops
 * the two from drifting into separate code paths that have to be fixed twice.
 */

/**
 * Per browser, per person. `localStorage` rather than a user setting on purpose: this is a
 * preference about *how someone types*, it costs nothing to re-pick, and a round trip to the
 * server for it would be a column, a mutation and a migration.
 */
const STORAGE_KEY = 'joyya.personCreator.newForm';

const readPreference = (): boolean => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        // Private browsing, or storage disabled. Fall back to the classic form — the safe
        // default is the one that was already there.
        return false;
    }
};

const PersonCreator = () => {
    const [useNewForm, setUseNewForm] = useState<boolean>(readPreference);

    const toggle = (checked: boolean) => {
        setUseNewForm(checked);
        try {
            window.localStorage.setItem(STORAGE_KEY, String(checked));
        } catch {
            // Not being able to remember the choice is not a reason to refuse making it.
        }
    };

    return (
        <ResponsiveContainer>
            <Segment style={{ padding: '1em 0em' }} vertical>
                <Container className="person-creator">
                    <div className="creator-card">
                        <div className="creator-top">
                            <Header as="h2" className="creator-header">Add person</Header>
                            <Checkbox
                                toggle
                                // Semantic only emits `id`/`htmlFor` when given an id; without
                                // one the switch has no accessible name.
                                id="person-creator-form-switch"
                                className="form-switch"
                                label="New registration form"
                                checked={useNewForm}
                                onChange={(_, data) => toggle(!!data.checked)}
                            />
                        </div>

                        {useNewForm ? <NewPersonCreator /> : <ClassicPersonCreator />}
                    </div>
                </Container>
            </Segment>
        </ResponsiveContainer>
    );
};

export { PersonCreator };
